import { useState } from 'react';
import { FilterList } from '@mui/icons-material';
import {
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil';
import { MHMButton } from '../MHM/MHMButton';
import { useTranslation } from '../../../contexts/TranslationContext';
import {
  DeviceMode,
  deviceModeAtom,
  filterStateAtom,
  selectedDeviceStateAtom,
} from '../../../models/atoms/device_atoms';
import { device_controller } from '../../../controllers/device_controller';
import { DevicesFilterList } from './DevicesFilterList';
import { useIsMobile } from '../../../hooks/useIsMobile';

export function DevicesFilter() {
  const { t } = useTranslation();
  const [filter, setFilterState] = useRecoilState(filterStateAtom);
  const setSelectedDevice = useSetRecoilState(selectedDeviceStateAtom);
  const resetFilter = useResetRecoilState(filterStateAtom);
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useRecoilState(deviceModeAtom);
  const isMobile = useIsMobile();

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setFilterState({ ...filter, searchText: value });
  }

  function handleChangeMode(
    _event: React.MouseEvent<HTMLElement>,
    newMode: DeviceMode | null,
  ) {
    if (newMode === null) {
      return;
    }

    setMode(newMode);
    handleResetFilter();
  }

  async function handleResetFilter() {
    resetFilter();
    await device_controller.get_device_list();
    setSelectedDevice(null);
  }

  if (isMobile) {
    return (
      <Stack
        gap={2}
        padding={1}
        position="relative"
        alignItems="center"
        justifyContent="center"
      >
        <ToggleButtonGroup
          value={mode}
          color="primary"
          exclusive
          onChange={handleChangeMode}
        >
          <ToggleButton value="live">{t('Live')}</ToggleButton>
          <ToggleButton value="archived">{t('Archived')}</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          label={t('Search devices by Device ID or Client ID')}
          value={filter.searchText}
          onChange={handleSearch}
          size="small"
          sx={{ width: '100%', flex: 1 }}
        />
        <Stack
          direction="row"
          gap={2}
          position="relative"
          sx={{ px: '1.7rem' }}
        >
          <div style={{ position: 'relative' }}>
            <MHMButton
              startIcon={<FilterList />}
              sx={{ width: '8rem', height: 40 }}
              onClick={() => setIsOpen(true)}
            >
              {t('Filter')}
            </MHMButton>
            <DevicesFilterList isOpen={isOpen} setIsOpen={setIsOpen} />
          </div>

          <MHMButton onClick={handleResetFilter} sx={{ width: '8rem' }}>
            {t('Clear Filter')}
          </MHMButton>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap={2} position="relative" sx={{ px: '1rem' }}>
      <ToggleButtonGroup
        value={mode}
        color="primary"
        size="small"
        exclusive
        onChange={handleChangeMode}
      >
        <ToggleButton value="live" style={{ lineHeight: 'normal' }}>
          Live
        </ToggleButton>
        <ToggleButton value="archived" style={{ lineHeight: 'normal' }}>
          Archived
        </ToggleButton>
      </ToggleButtonGroup>

      <TextField
        label={t('Search devices by Device ID or Client ID')}
        value={filter.searchText}
        onChange={handleSearch}
        size="small"
        sx={{ flex: 1, mr: 'auto' }}
      />

      <div style={{ position: 'relative' }}>
        <MHMButton
          startIcon={<FilterList />}
          sx={{ width: '8rem', height: 40 }}
          onClick={() => setIsOpen(true)}
        >
          {t('Filter')}
        </MHMButton>
        <DevicesFilterList isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>

      <MHMButton onClick={handleResetFilter} sx={{ width: '8rem' }}>
        {t('Clear Filter')}
      </MHMButton>
    </Stack>
  );
}
